'use client';

import {
  QueryKey,
  UseMutationOptions,
  UseQueryResult,
  useMutation,
  useQueries,
} from '@tanstack/react-query';

import { useAuth } from '@sola/auth-sdk/store.js';

import { MOCK_SUPPORTER } from '../constants';

export const useReadyForQueries = () => {
  const { authUser } = useAuth('authUser');
  return {
    isReady: !!MOCK_SUPPORTER || !!authUser?.authUserGlobalId,
    globalId: authUser?.authUserGlobalId,
  };
};

type AnySupporterQuery<TData = unknown> = {
  queryKey?: QueryKey;
  queryFn: (globalId: string) => Promise<TData>;
};

type InferSupporterQueryData<TQuery extends AnySupporterQuery> = Awaited<
  ReturnType<TQuery['queryFn']>
>;

type SupporterQueryTupleData<TQueries extends readonly AnySupporterQuery[]> = {
  [K in keyof TQueries]: TQueries[K] extends AnySupporterQuery
    ? InferSupporterQueryData<TQueries[K]> | undefined
    : never;
};

type SupporterQueryTupleResult<TQueries extends readonly AnySupporterQuery[]> =
  {
    data: SupporterQueryTupleData<TQueries>;
    isFetched: boolean;
    isFetching: boolean;
    isLoading: boolean;
    isPending: boolean;
    isPaused: boolean;
    isError: boolean;
    isSuccess: boolean;
    queries: ReturnType<typeof useQueries>;
  };

export function useSupporterQuery<TQuery extends AnySupporterQuery>(
  supporterQueries: TQuery,
): UseQueryResult<InferSupporterQueryData<TQuery>>;
export function useSupporterQuery<
  const TQueries extends readonly AnySupporterQuery[],
>(supporterQueries: TQueries): SupporterQueryTupleResult<TQueries>;
export function useSupporterQuery(
  supporterQueries: AnySupporterQuery | readonly AnySupporterQuery[],
):
  | UseQueryResult<unknown>
  | SupporterQueryTupleResult<readonly AnySupporterQuery[]> {
  const { isReady, globalId } = useReadyForQueries();
  const queries = Array.isArray(supporterQueries)
    ? supporterQueries
    : [supporterQueries];

  const queryResults = useQueries({
    queries: queries.map((supporterQuery) => {
      return {
        queryKey: supporterQuery.queryKey,
        // We should always have a globalId if this runs bc of isReady logic.
        queryFn: () => supporterQuery.queryFn(globalId as string),
        enabled: !!isReady,
        refetchOnWindowFocus: false,
      };
    }),
  });

  if (!Array.isArray(supporterQueries)) {
    return queryResults[0] as UseQueryResult<unknown>;
  }

  return {
    data: queryResults.map((result) => result.data),
    isFetched: queryResults.every((result) => result.isFetched),
    isFetching: queryResults.some((result) => result.isFetching),
    isLoading: queryResults.some((result) => result.isLoading),
    isPending: queryResults.some((result) => result.isPending),
    isPaused: queryResults.some((result) => result.isPaused),
    isError: queryResults.some((result) => result.isError),
    isSuccess: queryResults.every((result) => result.isSuccess),
    queries: queryResults,
  } as SupporterQueryTupleResult<readonly AnySupporterQuery[]>;
}

export const useSupporterMutation = <
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown,
>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
) => {
  return useMutation<TData, TError, TVariables, TContext>({
    ...options,
    ...(!!MOCK_SUPPORTER && {
      mutationFn: async () => {
        // eslint-disable-next-line no-promise-executor-return
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return true as TData;
      },
      onSuccess: () => alert('Success!'),
    }),
  });
};
